import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { getPhaseProgress } from '../../funnel/phaseProgress';
import { buildWhatsAppHref, buildWhatsAppMessage } from './whatsappMessage';

// Both widgets are code-split so the one that is NOT active never reaches the
// browser. LiveChat matters most: its SDK used to sit in the main bundle and
// injected cdn.livechatinc.com/tracking.js on every visit. Same pattern as the
// lazy admin/account routes in App.tsx.
const LiveChatWidget = lazy(() => import('./LiveChatWidget').then((m) => ({ default: m.LiveChatWidget })));
const WhatsAppWidget = lazy(() => import('./WhatsAppWidget').then((m) => ({ default: m.WhatsAppWidget })));

const WHATSAPP_DISMISSED_KEY = 'myl_whatsapp_dismissed';
/** Rendered size of LiveChat's minimized bubble (not configurable). */
const LIVECHAT_BUBBLE_SIZE_PX = 84;
const BUBBLE_SIZE_PROPERTY = '--chat-widget-bubble-size';
const MS_PER_SECOND = 1000;

function readDismissed(): boolean {
  try {
    return localStorage.getItem(WHATSAPP_DISMISSED_KEY) === '1';
  } catch {
    // Private mode / blocked storage: treat as not dismissed rather than hiding
    // the only support channel on the page.
    return false;
  }
}

/**
 * The gate: decides WHICH chat entry point exists and WHEN.
 *
 * Everything that could prevent a widget from being useful is checked here —
 * active widget, config readiness, phone present, current step, appearance
 * delay, previous dismissal — so the corresponding chunk is only requested
 * once it is actually going to be shown. A customer who closed the WhatsApp
 * button never downloads it again.
 *
 * Nothing mounts until the config has arrived: the default is 'livechat', so
 * rendering during the loading screen would pull LiveChat's third-party script
 * on sites that have WhatsApp selected, and then tear it down.
 */
export const ChatWidget: React.FC = () => {
  const { currentStep, formData } = useForm();
  const { config, configStatus, t } = useAppConfig();

  const rules = config.chat_widget_rules;
  const active = rules?.active_widget ?? 'livechat';
  const whatsapp = rules?.whatsapp;
  const phone = (config.contact_rules?.whatsapp_phone || '').trim();

  const [dismissed, setDismissed] = useState(readDismissed);
  const [delayElapsed, setDelayElapsed] = useState(false);

  const isReady = configStatus === 'ready';
  const delaySeconds = whatsapp?.delay_seconds ?? 0;

  useEffect(() => {
    if (!isReady || active !== 'whatsapp') return;
    if (delaySeconds <= 0) {
      setDelayElapsed(true);
      return;
    }
    // Timed from mount, not from each step change: restarting the wait on
    // every step would keep the button permanently out of reach.
    const timer = window.setTimeout(() => setDelayElapsed(true), delaySeconds * MS_PER_SECOND);
    return () => window.clearTimeout(timer);
  }, [isReady, active, delaySeconds]);

  const showWhatsApp =
    isReady &&
    active === 'whatsapp' &&
    Boolean(phone) &&
    Boolean(whatsapp) &&
    !dismissed &&
    delayElapsed &&
    (whatsapp?.visible_steps ?? []).includes(currentStep);

  const showLiveChat = isReady && active === 'livechat';

  // Publish the bubble footprint so .funnel-bottom-clearance reserves the right
  // gap — and none at all when no widget is active.
  useEffect(() => {
    const root = document.documentElement;
    const size = showLiveChat ? LIVECHAT_BUBBLE_SIZE_PX : showWhatsApp ? (whatsapp?.size_px ?? 0) : 0;
    root.style.setProperty(BUBBLE_SIZE_PROPERTY, `${size}px`);
    return () => {
      root.style.removeProperty(BUBBLE_SIZE_PROPERTY);
    };
  }, [showLiveChat, showWhatsApp, whatsapp?.size_px]);

  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(WHATSAPP_DISMISSED_KEY, '1');
    } catch {
      // Storage unavailable: hide it for this session at least.
    }
    setDismissed(true);
  }, []);

  // `{paso}` dice lo MISMO que el contador de la cabecera que el cliente está
  // mirando ("Perfil · 3/9", "Diseña tu menú · 2/4"), no la numeración interna
  // sobre 14: si el agente le pregunta por "el paso 11" el cliente no sabría de
  // qué le habla. Los textos de respaldo son los que usan FunnelLayout (fase 1)
  // y Phase2Header (fase 2), para que coincidan incluso sin traducción puesta.
  const pasoLabel = useMemo(() => {
    const progress = getPhaseProgress(currentStep);
    const fallback = progress.phase === 1 ? 'Perfil' : 'Diseña tu menú';
    return `${t(`funnel.phase_label_${progress.phase}`, fallback)} · ${progress.current}/${progress.total}`;
  }, [currentStep, t]);

  const href = useMemo(() => {
    if (!showWhatsApp || !whatsapp) return '';
    const message = buildWhatsAppMessage(whatsapp.message_template, {
      paso: pasoLabel,
      nombre: formData.nombre || undefined,
      especie: formData.animalType ? String(formData.animalType) : undefined,
    });
    return buildWhatsAppHref(phone, message);
  }, [showWhatsApp, whatsapp, pasoLabel, formData.nombre, formData.animalType, phone]);

  if (showLiveChat) {
    return (
      <Suspense fallback={null}>
        <LiveChatWidget />
      </Suspense>
    );
  }

  if (showWhatsApp && whatsapp) {
    return (
      <Suspense fallback={null}>
        <WhatsAppWidget
          config={whatsapp}
          href={href}
          onDismiss={whatsapp.show_close_button ? handleDismiss : undefined}
        />
      </Suspense>
    );
  }

  return null;
};
